import { type ScreensList, useNavigation } from '@shared-core/navigation'
import { CircleIcon } from '@shared-ui/components/ui/app-icon'
import { Container, Row } from '@shared-ui/components/ui/grid'
import { GText } from '@shared-ui/components/ui/text'
import { useExitCross } from '@shared-ui/hooks/navigate/use-exit-cross'
import { useHardwareBackPress } from '@shared-ui/hooks/rn/use-hardware-back-press'
import type { PropsWithChildren, ReactNode } from 'react'
import { type TextStyle, View, type ViewStyle } from 'react-native'

import { useCreateStyles } from './styles'

// Режим крестика выхода из флоу (meta `__exitFlow` / hop).
// - auto (default) — показать при hop >= 2 (или entry на flow-root)
// - always — всегда (экраны без back / лоадеры); нужен exitCrossFallback на холодном входе
// - false — не показывать авто-крест (кастом через onCloseHandler)
export type HeaderExitCrossMode = 'auto' | 'always' | false

type ComponentProps = {
  title?: ReactNode | string
  subtitle?: ReactNode | string
  testID?: string
  isBackButton?: boolean
  isInFormSheet?: boolean
  onBackHandler?: () => boolean
  // Явный обработчик крестика. Если ключ передан — авто-exitCross не используется.
  onCloseHandler?: () => void
  // Авто-крест по hop/origin. По умолчанию auto.
  exitCross?: HeaderExitCrossMode
  // Fallback origin при exitCross="always" / холодном входе без meta.
  exitCrossFallback?: ScreensList
  backButtonStyle?: ViewStyle
  backButtonIconStyle?: TextStyle
  closeButtonStyle?: ViewStyle
  closeIconSize?: number
  containerStyle?: ViewStyle
  contentStyle?: ViewStyle
}

type NavigationLike = {
  canGoBack: () => boolean
  goBack: () => void
}

// Резолв крестика Header: явный onCloseHandler / always / auto по hop.
function resolveExitCrossHandler(params: {
  exitCross: HeaderExitCrossMode | undefined
  hasExplicitClose: boolean
  explicitClose: (() => void) | undefined
  showExitCross: boolean
  onExitFlow: () => void
}): (() => void) | undefined {
  if (params.hasExplicitClose) {
    return params.explicitClose
  }

  if (params.exitCross === false) {
    return undefined
  }

  if (params.exitCross === 'always') {
    return params.onExitFlow
  }

  // default: auto
  return params.showExitCross ? params.onExitFlow : undefined
}

export function Header(props: PropsWithChildren<ComponentProps>) {
  const styles = useCreateStyles()
  const navigation = useNavigation<NavigationLike>()
  const { showExitCross, onExitFlow } = useExitCross({ fallbackOrigin: props.exitCrossFallback })

  const resolvedCloseHandler = resolveExitCrossHandler({
    exitCross: props.exitCross,
    hasExplicitClose: Object.hasOwn(props, 'onCloseHandler'),
    explicitClose: props.onCloseHandler,
    showExitCross,
    onExitFlow,
  })

  // общая функция назад
  // дополнительно проверяем onBackHandler, если он вернет что-то негативное,
  // то функция не будет навигировать назад.
  const handleBack = () => {
    if (props.isBackButton !== true) {
      return
    }

    if (props.onBackHandler) {
      const isNext = props.onBackHandler()

      if (!isNext) {
        return
      }
    }

    if (navigation.canGoBack() && props.isBackButton) {
      navigation.goBack()
    }
  }

  const handleClose = () => {
    resolvedCloseHandler?.()
  }

  // handler for android native back button
  useHardwareBackPress(() => {
    if (navigation.canGoBack() && props.isBackButton) {
      navigation.goBack()
      return true
    }

    return false
  }, [props.isBackButton])

  return (
    <Container style={props.containerStyle}>
      <View style={[styles.row, props.isInFormSheet && styles.isInFormSheet, props.contentStyle]}>
        {props.isBackButton && (
          <CircleIcon
            style={[styles.actions, props.backButtonStyle]}
            onPress={handleBack}
            icon="chevron-left"
            styleIcon={props.backButtonIconStyle}
          />
        )}
        {!props.isBackButton && <View style={[styles.actions, props.backButtonStyle]} />}

        <View style={styles.wrapperHeaderCenter}>
          {typeof props.title === 'string' && (
            <GText
              size={18}
              fw={600}
              lh={21.6}
              align="center"
              style={styles.headerStyleCenter}
              ellipsizeMode="tail"
              numberOfLines={1}
              testID={props.testID}
            >
              {props.title || ''}
            </GText>
          )}
          {typeof props.title !== 'string' && props.title ? props.title : null}
          {typeof props.subtitle === 'string' && (
            <GText variant="caption-13-regular" isSecondary numberOfLines={1}>
              {props.subtitle}
            </GText>
          )}
          {typeof props.subtitle !== 'string' && props.subtitle}
        </View>
        <Row align="center" style={styles.actions}>
          {props.children}
          {!!resolvedCloseHandler && (
            <CircleIcon
              size={props.closeIconSize ?? 24}
              icon="close"
              onPress={handleClose}
              style={props.closeButtonStyle}
            />
          )}
        </Row>
      </View>
    </Container>
  )
}
